import { z } from "zod";
import { MAX_TRACKED_JOB_ID_LENGTH } from "./trackedJobIdLimit";

export const LEGACY_WORKFLOW_JOB_PREFIX = "workflow_result:control_plane:";
const sourceId = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
export const completionJobIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/);
// Workflow monitors also track document aliases, whose creation contract allows 512 characters.
export { MAX_TRACKED_JOB_ID_LENGTH };
export const trackedJobIdSchema = z.string().min(1).max(MAX_TRACKED_JOB_ID_LENGTH).regex(/^[^\u0000-\u001f\u007f]+$/).refine(value => value === value.trim());
export const legacyWorkflowReferenceSchema = z.object({ sourceId, docId: trackedJobIdSchema, sourceRevision: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional() }).strict();
export type LegacyWorkflowReference = z.infer<typeof legacyWorkflowReferenceSchema>;
export function legacyWorkflowJobId(value: string) { return `${LEGACY_WORKFLOW_JOB_PREFIX}${sourceId.parse(value)}`; }
export function legacyWorkflowSourceId(jobId: string): string | null {
  if (!jobId.startsWith(LEGACY_WORKFLOW_JOB_PREFIX)) return null;
  const result = sourceId.safeParse(jobId.slice(LEGACY_WORKFLOW_JOB_PREFIX.length));
  return result.success ? result.data : null;
}
export const jobCompletionRequestSchema = z.object({
  jobIds: z.array(trackedJobIdSchema).min(1).max(100),
  legacySources: z.array(legacyWorkflowReferenceSchema.extend({ jobId: trackedJobIdSchema })).max(100).optional(),
}).superRefine((value, ctx) => {
  const seen = new Set<string>();
  for (const ref of value.legacySources ?? []) {
    if (!value.jobIds.includes(ref.jobId) || seen.has(ref.jobId)) ctx.addIssue({ code: "custom", message: "Legacy source does not identify one requested job" });
    seen.add(ref.jobId);
  }
});

/** Tracking aliases stay in the monitor; source references pin completion requests. */
export function buildJobCompletionRequest(jobs: readonly { jobId: string; legacySource?: LegacyWorkflowReference }[]) {
  const legacySources = jobs.flatMap((job) => job.legacySource ? [{ ...job.legacySource, jobId: job.jobId }] : []);
  return jobCompletionRequestSchema.parse({ jobIds: jobs.map((job) => job.jobId), ...(legacySources.length ? { legacySources } : {}) });
}
